import { createContext, useCallback, useContext, useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle, Info, X } from "lucide-react";

const ToastContext = createContext({ showToast: () => {} });

const ICONS = {
  success: CheckCircle2,
  warning: AlertTriangle,
  error: XCircle,
  info: Info,
};

const TYPE_CLASS = {
  success: "kipa-toast-success",
  warning: "kipa-toast-warning",
  error: "kipa-toast-error",
  info: "kipa-toast-info",
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  const showToast = useCallback((message, type = "info", duration = 3000) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, message, type, exiting: false }]);
    setTimeout(() => {
      setToasts((t) =>
        t.map((x) => (x.id === id ? { ...x, exiting: true } : x))
      );
      setTimeout(() => dismiss(id), 150);
    }, duration);
  }, [dismiss]);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="kipa-toast-host" aria-live="polite">
        {toasts.map((t) => {
          const Icon = ICONS[t.type] || Info;
          return (
            <div
              key={t.id}
              className={`kipa-toast ${TYPE_CLASS[t.type]} ${t.exiting ? "kipa-toast-exit" : "kipa-toast-enter"}`}
              role="status"
            >
              <Icon size={16} className="kipa-toast-icon" aria-hidden />
              <span className="kipa-toast-msg">{t.message}</span>
              <button
                type="button"
                className="kipa-toast-close"
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss"
              >
                <X size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useKipaToast() {
  return useContext(ToastContext);
}

/** Replace window.alert with toast in dev flows */
export function toastAlert(message, type = "error") {
  if (typeof window !== "undefined" && window.__kipaToast) {
    window.__kipaToast(message, type);
  }
}
